// BHP-1053: the navbar's active-customer control, against Backend2.0. Lists the
// account's customers (customer-scoped collection) and switches the active one
// via the persistent Zustand store — replacing the legacy Redux/CustomerService
// + localStorage bootstrap. Changing the store re-drives ActiveCustomerProvider,
// which refreshes the app-wide context.
import { Dropdown } from 'react-bootstrap';
import { useCustomers } from '../application/customerQueries';
import { useActiveCustomer } from '../application/useActiveCustomer';
import { useActiveCustomerStore } from '../application/activeCustomerStore';
import type { Customer } from '../domain/customer';

export default function CustomerSwitcher(): JSX.Element {
  const { data, isPending } = useCustomers();
  const { isFetching } = useActiveCustomer();
  const activeUuid = useActiveCustomerStore(
    (state) => state.activeCustomerUuid
  );
  const setActiveCustomer = useActiveCustomerStore(
    (state) => state.setActiveCustomer
  );

  if (isPending) {
    return label('loading');
  }

  const customers = data?.items ?? [];
  if (customers.length === 0) {
    return label('-');
  }

  const active =
    customers.find((customer) => customer.uuid === activeUuid) ?? customers[0];

  if (customers.length === 1) {
    return label(displayName(active));
  }

  return (
    <Dropdown>
      <span className="block">Active customer,</span>
      <Dropdown.Toggle
        variant="none"
        as="a"
        id="dropdown-customer"
        className="user-name"
      >
        <strong className="text-teal-600">{displayName(active)}</strong>
        {isFetching && (
          <span
            className="spinner-border spinner-border-sm ms-1"
            role="status"
            data-testid="customer-switch-spinner"
          />
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-menu-right account">
        {customers.map((customer) => (
          <Dropdown.Item
            key={customer.uuid}
            active={customer.uuid === active.uuid}
            onClick={() => {
              setActiveCustomer(customer.uuid);
            }}
          >
            {displayName(customer)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function label(value: string): JSX.Element {
  return (
    <>
      <span>Active customer,</span>
      <br />
      <strong className="text-teal-600">{value}</strong>
    </>
  );
}

function displayName(customer: Customer): string {
  return customer.companyName ?? customer.administrationFullName;
}
